import i18next from 'i18next'
import React from 'react'
import { formatDateDDMonthYYYY } from '../../../../../utils/date'
import { Image } from '../../../../../utils/image'
import { relatedContentSet, relatedContentSrc } from '../../../../../utils/imageSizes'
import { Link } from '../../../../../utils/link'
import { relatedContentBlockClasses } from '../../../../../utils/style'
import { getClassName, slugify } from '../../../../../utils/utils'
import { useInView } from '../../../../hooks/useInView'
import { InviewOptions } from '../../blocksState'
import { NewsOrEvent } from './relatedContentBlockState'
import { DecorativeIcon } from '../../../../../utils/spaarneIcons'

export type RelatedContentBlockStyle = Partial<{
  blockClassName: string
  blockWithHeaderClassName: string
  blockWithLinkClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  listClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridLinkClassName: string
  gridHeaderClassName: string
  gridContentClassName: string
  gridTitleClassName: string
  gridLabelClassName: string
  gridIntroClassName: string
  gridFauxLinkWrapperClassName: string
  gridFauxLinkClassName: string
  gridFauxLinkLabelClassName: string
  gridVisualClassName: string
  gridImageClassName: string
  gridPictureClassName: string
  gridDateClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickableClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconId: string,
  contentRowClassName: string,
  dossierClassName: string,
}>

type RelatedContentBlockProps = {
  index: number
  inview?: InviewOptions
  title?: string
  intro?: string
  items: NewsOrEvent[]
  readMoreNewsUrl?: string
  readMoreAgendaUrl?: string
  pagination: boolean
  onLoadMore?: () => void
}

export const RelatedContentBlockLayout = (props: RelatedContentBlockProps) => {
  const { index, inview, intro, items, title, readMoreNewsUrl, readMoreAgendaUrl, pagination, onLoadMore } = props
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("RelatedContentBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  const readMoreNews = props.items.length > 0 && readMoreNewsUrl
  const readMoreAgenda = props.items.length > 0 && readMoreAgendaUrl

  return items.length > 0 ? (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(relatedContentBlockClasses, 'blockClassName', 'container') + ' ' + (inView ? getClassName(relatedContentBlockClasses, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(relatedContentBlockClasses, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(relatedContentBlockClasses, 'headerClassName', '')}>
            {title && <h2 className={getClassName(relatedContentBlockClasses, 'titleClassName', 'mb-3')}>{title}</h2>}
            {intro && <p className={getClassName(relatedContentBlockClasses, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {items.length > 0 && (
          <div className={getClassName(relatedContentBlockClasses, 'contentClassName', '')}>
            <ul className={getClassName(relatedContentBlockClasses, 'listClassName', 'list-unstyled mb-0 row')}>
              {items.map((item) => (
                <RelatedContentItemLayout {...item} key={`rc-${slugify(item.url)}`} />
              ))}
            </ul>
          </div>
        )}

        {!pagination && (readMoreNews || readMoreAgenda) && (
          <footer className={getClassName(relatedContentBlockClasses, 'footerClassName', 'text-center')}>
            <div className={getClassName(relatedContentBlockClasses, 'contentRowClassName', 'd-inline-block')}>
              {readMoreAgenda && (
                <div className={getClassName(relatedContentBlockClasses, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    url={readMoreAgendaUrl}
                    className={getClassName(relatedContentBlockClasses, 'clickableClassName', 'btn btn-dark d-inline-flex')}
                  >
                    <span className={getClassName(relatedContentBlockClasses, 'clickableLabelClassName', '')}>
                      {i18next.t('misc:viewMoreEvents')}
                    </span>
                    <svg
                      className={getClassName(relatedContentBlockClasses, 'clickableIconClassName', 'ml-3')}
                      aria-hidden="true"
                      width="20px"
                      height="20px"
                    >
                      <use xlinkHref={getClassName(relatedContentBlockClasses, 'iconId', '#icon--chevron-right-large')} />
                    </svg>
                  </Link>
                </div>
              )}
              {readMoreNews && (
                <div className={getClassName(relatedContentBlockClasses, 'clickableWrapperClassName', 'd-inline-block')}>
                  <Link
                    url={readMoreNewsUrl}
                    className={getClassName(relatedContentBlockClasses, 'clickableClassName', 'btn btn-dark d-inline-flex')}
                  >
                    <span className={getClassName(relatedContentBlockClasses, 'clickableLabelClassName', '')}>
                      {i18next.t('misc:viewMoreNews')}
                    </span>
                    <svg
                      className={getClassName(relatedContentBlockClasses, 'clickableIconClassName', 'ml-3')}
                      aria-hidden="true"
                      width="20px"
                      height="20px"
                    >
                      <use xlinkHref={getClassName(relatedContentBlockClasses, 'iconId', '#icon--chevron-right-large')} />
                    </svg>
                  </Link>
                </div>
              )}
            </div>
          </footer>
        )}

        {pagination && (
          <div className="block__pagination">
            <button type="button" onClick={onLoadMore} className="button--primary">
              <span className="button__label">{readMoreNews || readMoreAgenda}</span>
            </button>
          </div>
        )}
      </div>
    </section>
  ) : (
    <></>
  )
}

export const RelatedContentItemLayout = (p: NewsOrEvent) => (
  <li className={getClassName(relatedContentBlockClasses, 'gridItemClassName', 'col-md-6 col-lg-4 py-3')}>
    <article className={getClassName(relatedContentBlockClasses, 'gridArticleClassName', '')}>
      <Link
        url={p.url}
        className={getClassName(
          relatedContentBlockClasses,
          'gridLinkClassName',
          'btn btn-link w-100 text-left border-bottom rounded-0 pl-0'
        )}
      >
        <div className={getClassName(relatedContentBlockClasses, 'gridContentClassName', 'card-body px-0 pb-0')}>
          <header className={getClassName(relatedContentBlockClasses, 'gridHeaderClassName', '')}>
            {p.title && (
              <h3 className={getClassName(relatedContentBlockClasses, 'gridTitleClassName', 'mb-2')}>
                {p.title}
                {p.eventDate && <span className="sr-text"> ({formatDateDDMonthYYYY(p.eventDate)})</span>}
              </h3>
            )}
          </header>

          {p.intro && <p className={getClassName(relatedContentBlockClasses, 'gridIntroClassName', '')}>{p.intro}</p>}

          <footer
            className={getClassName(
              relatedContentBlockClasses,
              'gridFauxLinkWrapperClassName',
              'd-inline-flex align-items-center'
            )}
            aria-hidden="true"
          >
            <div className={getClassName(relatedContentBlockClasses, 'gridFauxLinkClassName', 'btn btn-link px-0')}>
              <span className={getClassName(relatedContentBlockClasses, 'gridFauxLinkLabelClassName', '')}>
                {i18next.t('common:readMore')}
              </span>
            </div>
          </footer>
        </div>

        <div className={getClassName(relatedContentBlockClasses, 'gridVisualClassName', 'card-img-top')}>
          {p.image && (
            <Image
              {...p.image}
              src={relatedContentSrc}
              srcset={relatedContentSet}
              classNames={{ image: getClassName(relatedContentBlockClasses, 'gridImageClassName', 'card-img-top') }}
              width="392"
              height="196"
              lazyload={true}
              sizes="(max-width: 576px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 392px"
            />
          )}
          {p?.dossier && (
            <span className={getClassName(relatedContentBlockClasses, 'dossierClassName', 'badge')}>
              <DecorativeIcon iconId={"icon--folders"} width="24px" height="24px" />
              <span>{i18next.t("dossier:DossierTag")}</span>
            </span>
          )}
          {p.eventDate && (
            <time
              className={getClassName(relatedContentBlockClasses, 'gridDateClassName', '')}
              dateTime={p.eventDate.toISOString()}
            >
              <span>
                {p.eventDate.toLocaleDateString('nl-NL', {
                  day: '2-digit',
                })}
              </span>
              <span>
                {p.eventDate.toLocaleDateString('nl-NL', {
                  month: 'short',
                })}
              </span>
            </time>
          )}
        </div>
      </Link>
    </article>
  </li>
)
